import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, ChevronDown, ChevronRight, Edit2, Save, X, Tag, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ─── types ────────────────────────────────────────────────────────────────────

interface Keyword {
  id: string;
  keyword: string;
  weight: number;
}

interface Example {
  id: string;
  text: string;
}

interface NlpIntent {
  id: string;
  name: string;
  category: string;
  description: string;
  confidenceThreshold: number;
  responseGuidance: string | null;
  routingDepartment: string | null;
  isActive: boolean;
  createdAt: string;
  keywords: Keyword[];
  examples: Example[];
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const api = {
  list:           () => apiRequest("GET", "/api/nlp/intents").then(r => r.json()),
  create:         (body: object) => apiRequest("POST", "/api/nlp/intents", body),
  update:         (id: string, body: object) => apiRequest("PUT", `/api/nlp/intents/${id}`, body),
  delete:         (id: string) => apiRequest("DELETE", `/api/nlp/intents/${id}`),
  addKeyword:     (id: string, body: object) => apiRequest("POST", `/api/nlp/intents/${id}/keywords`, body),
  removeKeyword:  (id: string, kwId: string) => apiRequest("DELETE", `/api/nlp/intents/${id}/keywords/${kwId}`),
  addExample:     (id: string, body: object) => apiRequest("POST", `/api/nlp/intents/${id}/examples`, body),
  removeExample:  (id: string, exId: string) => apiRequest("DELETE", `/api/nlp/intents/${id}/examples/${exId}`),
};

// ─── create/edit dialog ───────────────────────────────────────────────────────

function IntentDialog({
  open, onClose, intent,
}: {
  open: boolean;
  onClose: () => void;
  intent?: NlpIntent;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const editing = !!intent;

  const [form, setForm] = useState({
    name:                intent?.name ?? "",
    category:            intent?.category ?? "",
    description:         intent?.description ?? "",
    confidenceThreshold: intent?.confidenceThreshold ?? 0.25,
    responseGuidance:    intent?.responseGuidance ?? "",
    routingDepartment:   intent?.routingDepartment ?? "",
    isActive:            intent?.isActive ?? true,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        confidenceThreshold: Number(form.confidenceThreshold),
      };
      const res = editing
        ? await api.update(intent!.id, body)
        : await api.create(body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err?.error ?? "Failed to save intent.");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nlp-intents"] });
      toast({ title: editing ? "Intent updated" : "Intent created" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const set = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Intent" : "New Intent"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="e.g. application_status" />
            </div>
            <div className="space-y-1">
              <Label>Category <span className="text-destructive">*</span></Label>
              <Input value={form.category} onChange={e => set("category", e.target.value)}
                placeholder="e.g. Admissions" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder="What does this intent represent?" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Confidence Threshold</Label>
              <Input type="number" min={0.05} max={1} step={0.05}
                value={form.confidenceThreshold}
                onChange={e => set("confidenceThreshold", e.target.value)} />
              <p className="text-xs text-muted-foreground">Score 0–1; default 0.25</p>
            </div>
            <div className="space-y-1">
              <Label>Routing Department</Label>
              <Input value={form.routingDepartment}
                onChange={e => set("routingDepartment", e.target.value)}
                placeholder="e.g. Admissions Office" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Response Guidance</Label>
            <Textarea rows={2} value={form.responseGuidance}
              onChange={e => set("responseGuidance", e.target.value)}
              placeholder="Hint injected into the LLM system prompt when this intent is detected." />
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={form.isActive} onCheckedChange={v => set("isActive", v)} />
            <Label>Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : editing ? "Save changes" : "Create intent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── keyword/example inline management ───────────────────────────────────────

function KeywordPanel({ intent }: { intent: NlpIntent }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [kw, setKw]  = useState("");
  const [wt, setWt]  = useState("1");

  const add = useMutation({
    mutationFn: () => api.addKeyword(intent.id, { keyword: kw.trim(), weight: Number(wt) }),
    onSuccess: async (res) => {
      if (!res.ok) { toast({ title: "Error adding keyword", variant: "destructive" }); return; }
      qc.invalidateQueries({ queryKey: ["nlp-intents"] });
      setKw(""); setWt("1");
    },
  });

  const remove = useMutation({
    mutationFn: (kwId: string) => api.removeKeyword(intent.id, kwId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nlp-intents"] }),
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {intent.keywords.map(k => (
          <span key={k.id}
            className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs">
            {k.keyword}
            {k.weight !== 1 && <span className="text-muted-foreground">×{k.weight}</span>}
            <button onClick={() => remove.mutate(k.id)} className="hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input className="h-7 text-xs" placeholder="keyword or phrase" value={kw}
          onChange={e => setKw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && kw.trim() && add.mutate()} />
        <Input className="h-7 w-16 text-xs" type="number" min={0.1} step={0.1}
          value={wt} onChange={e => setWt(e.target.value)} placeholder="wt" />
        <Button size="sm" className="h-7 px-2" onClick={() => add.mutate()}
          disabled={!kw.trim() || add.isPending}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function ExamplePanel({ intent }: { intent: NlpIntent }) {
  const qc = useQueryClient();
  const [txt, setTxt] = useState("");

  const add = useMutation({
    mutationFn: () => api.addExample(intent.id, { text: txt.trim() }),
    onSuccess: async (res) => {
      if (!res.ok) return;
      qc.invalidateQueries({ queryKey: ["nlp-intents"] });
      setTxt("");
    },
  });

  const remove = useMutation({
    mutationFn: (exId: string) => api.removeExample(intent.id, exId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nlp-intents"] }),
  });

  return (
    <div className="space-y-1">
      {intent.examples.map(ex => (
        <div key={ex.id} className="flex items-center justify-between rounded border px-2 py-1 text-xs">
          <span className="text-muted-foreground">"{ex.text}"</span>
          <button onClick={() => remove.mutate(ex.id)} className="hover:text-destructive ml-2">
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input className="h-7 text-xs" placeholder="Sample utterance…" value={txt}
          onChange={e => setTxt(e.target.value)}
          onKeyDown={e => e.key === "Enter" && txt.trim() && add.mutate()} />
        <Button size="sm" className="h-7 px-2" onClick={() => add.mutate()}
          disabled={!txt.trim() || add.isPending}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// ─── intent card ──────────────────────────────────────────────────────────────

function IntentCard({ intent }: { intent: NlpIntent }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing]   = useState(false);
  const [tab, setTab]           = useState<"keywords" | "examples">("keywords");

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(intent.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nlp-intents"] });
      toast({ title: "Intent deleted" });
    },
  });

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setExpanded(e => !e)}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} intent ${intent.name}`}
            >
              {expanded ? <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" /> : <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />}
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold truncate">{intent.name}</span>
                <Badge variant="secondary" className="text-xs">{intent.category}</Badge>
                {!intent.isActive && <Badge variant="outline" className="text-xs text-muted-foreground">inactive</Badge>}
              </div>
              {intent.description && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{intent.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {intent.keywords.length} kw · {intent.examples.length} ex · conf {intent.confidenceThreshold}
            </span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} aria-label={`Edit intent ${intent.name}`}>
              <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              aria-label={`Delete intent ${intent.name}`}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-4 px-4 space-y-3">
          {intent.responseGuidance && (
            <p className="text-xs bg-muted rounded p-2">
              <span className="font-medium">Response guidance:</span> {intent.responseGuidance}
            </p>
          )}
          {intent.routingDepartment && (
            <p className="text-xs text-muted-foreground">
              Routes to: <span className="font-medium">{intent.routingDepartment}</span>
            </p>
          )}

          {/* Tab switcher */}
          <div className="flex gap-1 border-b">
            {(["keywords", "examples"] as const).map(t => (
              <button key={t}
                className={`px-3 py-1 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setTab(t)}>
                {t === "keywords" ? <><Tag className="inline h-3 w-3 mr-1" />Keywords</> : <><MessageSquare className="inline h-3 w-3 mr-1" />Examples</>}
              </button>
            ))}
          </div>

          {tab === "keywords" ? <KeywordPanel intent={intent} /> : <ExamplePanel intent={intent} />}
        </CardContent>
      )}

      {editing && <IntentDialog open intent={intent} onClose={() => setEditing(false)} />}
    </Card>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function NlpIntentsPage() {
  const [creating, setCreating] = useState(false);
  const [search,   setSearch]   = useState("");
  const [catFilter, setCat]     = useState("");

  const { data: intents = [], isLoading } = useQuery<NlpIntent[]>({
    queryKey: ["nlp-intents"],
    queryFn:  api.list,
  });

  const categories = [...new Set(intents.map(i => i.category))].sort();
  const filtered = intents.filter(i => {
    const q = search.toLowerCase();
    const matchSearch = !q || i.name.includes(q) || i.category.toLowerCase().includes(q)
      || i.description.toLowerCase().includes(q)
      || i.keywords.some(k => k.keyword.toLowerCase().includes(q));
    const matchCat = !catFilter || i.category === catFilter;
    return matchSearch && matchCat;
  });

  const grouped = filtered.reduce<Record<string, NlpIntent[]>>((acc, i) => {
    (acc[i.category] ??= []).push(i);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">NLP Intents</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Keyword-scored intents that classify incoming messages and inject context into the AI system prompt.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Intent
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Input className="w-60" placeholder="Search intents…" value={search}
          onChange={e => setSearch(e.target.value)} />
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant={catFilter === "" ? "default" : "outline"}
            onClick={() => setCat("")}>All</Button>
          {categories.map(c => (
            <Button key={c} size="sm" variant={catFilter === c ? "default" : "outline"}
              onClick={() => setCat(c)}>{c}</Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading intents…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground">
          {intents.length === 0
            ? "No intents yet — click \"New Intent\" to create one."
            : "No intents match your search."}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, items]) => (
            <div key={cat} className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{cat}</h2>
              <div className="space-y-2">
                {items.map(intent => <IntentCard key={intent.id} intent={intent} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <IntentDialog open onClose={() => setCreating(false)} />}
    </div>
  );
}
